import { useDispatch } from 'react-redux';
import { AccordionDetails, AccordionSummary, Typography } from '@mui/material';
import { useAuth0 } from '@auth0/auth0-react';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import moment from 'moment';

import { FHAccordion } from '../../../../base';
import TargetProgressRow from './TargetProgressRow';
import { parentSupervisionActions } from '../../../../../store';
import { FORTA_BLUE, FORTA_WHITE } from '../../../../../utils/colors';

import type { ParentGoalTarget } from './ParentTargetProgress';

interface GoalTargetRowProps {
  isEditable: boolean;
  goalName: string;
  dataLoading: boolean;
  parentSupervisionId: number;
  isSupervisionCompleted: boolean;
  targets: ParentGoalTarget[];
  dataCollectionMethod: string;
}

function GoalRow({
  isSupervisionCompleted,
  parentSupervisionId,
  isEditable,
  dataLoading,
  goalName,
  targets,
  dataCollectionMethod,
}: GoalTargetRowProps) {
  const dispatch = useDispatch();
  const { getAccessTokenSilently } = useAuth0();

  const handleDeleteEvaluation = (evaluationId: number) => {
    dispatch(
      parentSupervisionActions.deleteTargetEvaluation({
        getAccessTokenSilently,
        parentSupervisionId,
        targetEvaluationId: evaluationId,
      }),
    );
  };
  const handleSaveEvaluation = (data: { [key: string]: number }, evaluationId: number) => {
    dispatch(
      parentSupervisionActions.updateTargetEvaluation({
        getAccessTokenSilently,
        parentSupervisionId,
        targetEvaluationId: evaluationId,
        data,
      }),
    );
  };
  const handleUpdateRateEvent = async (
    data: { date_of_event: moment.Moment },
    trailId: number,
    rateEventId: number | null,
  ) => {
    if (rateEventId) {
      await dispatch(
        parentSupervisionActions.updateRateEvent({
          getAccessTokenSilently,
          parentSupervisionId,
          trailId,
          rateEventId,
          data: {
            date_of_event: moment(data.date_of_event).toISOString(),
          },
        }),
      ).unwrap();
    } else {
      await dispatch(
        parentSupervisionActions.createRateEvent({
          getAccessTokenSilently,
          parentSupervisionId,
          trailId,
          data: {
            date_of_event: moment(data.date_of_event).toISOString(),
          },
        }),
      ).unwrap();
    }
    return true;
  };
  const handleDeleteRateEvent = (trailId: number, rateEventId: number) => {
    dispatch(
      parentSupervisionActions.deleteRateEvent({
        getAccessTokenSilently,
        parentSupervisionId,
        trailId,
        rateEventId,
      }),
    );
  };
  return (
    <FHAccordion
      defaultExpanded={true}
      sx={{ boxShadow: 'none', backgroundColor: FORTA_WHITE, p: 1 }}
    >
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Typography ml={1} mt={0.5} textAlign="left" fontSize={21} color={FORTA_BLUE.primary}>
          {goalName}
        </Typography>
      </AccordionSummary>
      <AccordionDetails sx={{ ml: 1 }}>
        <Typography textAlign="left" fontSize={16} fontWeight={600} color={FORTA_BLUE.primary}>
          {targets?.length > 1 ? 'Targets' : 'Target'}
        </Typography>
        {targets.map((target, i) => (
          <TargetProgressRow
            key={target.id ?? i}
            index={i}
            target={target}
            dataLoading={dataLoading}
            dataCollectionMethod={dataCollectionMethod}
            isEditable={isEditable}
            isFirstRow={i === 0}
            isLastRow={i === targets.length - 1}
            isSupervisionCompleted={isSupervisionCompleted}
            onDelete={handleDeleteEvaluation}
            onUpdate={handleSaveEvaluation}
            onUpdateRateEvent={handleUpdateRateEvent}
            onDeleteRateEvent={handleDeleteRateEvent}
          />
        ))}
      </AccordionDetails>
    </FHAccordion>
  );
}

export default GoalRow;
